import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import "react-responsive-pagination/themes/classic.css";

import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { debounce, truncate } from "lodash";
import { dashboardActions } from "redux/Ldb/actions";
import BundledForecastGraph from "./BundledForecastGraph";
import SingleForecastGraph from "./SingleForecastGraph";
import BundledForecastTable from "./BundledForecastTable";
import { forecastPeriodOptions } from "variables/forecastPeriodOptions";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import useGetAllWarehouses from "hooks/useGetAllWarehouses";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import SingleForecastTable from "./SingleForecastTable";
import { FORECAST_VIEW_TYPE, FORECAST_SKU_TYPE } from "./constant/forecastConstant";
import Button from "components/common/Button";
import { AccountPermissions } from "types/permissions";

import useToggle from "hooks/useToggle";
import ForecastNewProductModal from "./ForecastNewProductModal";
import ForecastConfiguration from "./ForecastConfiguration";
import SalesTargetAgentModal from "components/views/Synth2.0/ChatAgent/SalesAgent/SalesTargetAgentModal";
import SalesTargetAgent from "components/views/Synth2.0/ChatAgent/SalesAgent/SalesAgent";
import useGetAllAgent from "hooks/workforceHooks/useGetAllAgent";
import { AgentType } from "enums/AgentTypeEnum";
import { use } from "echarts";

const FinishedGoodsForecast = () => {
    const [activeForecastViewType, setActiveForecastViewType] = useState<number>(
        FORECAST_VIEW_TYPE.GRAPH
    );
    const [activeSkuType, setActiveSkuType] = useState<number>(FORECAST_SKU_TYPE.SINGLE);

    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [customValue, customOnChange] = useState("");
    const [singleProductChannelFilter, setSingleProductChannelFilter] = useState<string>("Shopify");
    const [bundleProductChannelFilter, setBundleProductChannelFilter] = useState<string>("Shopify");
    const [warehouse, setWarehouse] = useState<string>("");
    const [configureForecast, setConfigureForecast] = useState<boolean>(false);

    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedBundleDate, setSelectedBundleDate] = useState<string>("");
    const [productSearch, setProductSearch] = useState<string>("");
    const [debouncedProductSearch, setDebouncedProductSearch] = useState<string>("");
    const [bundleSearch, setBundleSearch] = useState<string>("");
    const [debouncedBundleSearch, setDebouncedBundleSearch] = useState<string>("");
    const [agentModal, setAgentModal] = useState<boolean>(false);
    const [openSalesTargetAgent, setOpenSalesTargetAgent] = useState<boolean>(false);

    const { fetchSingleAgentSuccess, fetchingSingleAgent, updatingAiTeammate } = useAppSelector(
        (state) => state?.workforce
    );
    const getAllAgentsQuery = useGetAllAgent({
        agentType: AgentType.TargetAlly,
    });

    const targetAllyAgent = getAllAgentsQuery.data?.find(
        (agent) => agent.type === AgentType.TargetAlly
    );

    const warehouseRef = useRef(null);

    const [openForecastNewProductModal, toggleForecastNewProductModal] = useToggle();

    const startDate = moment().format("YYYY-MM-DD");

    const forecastTabs = [
        { id: FORECAST_VIEW_TYPE.GRAPH, title: "Graph View" },
        { id: FORECAST_VIEW_TYPE.TABLE, title: "Table View" },
    ];

    const dispatch = useAppDispatch();
    const { addingForecastConfig, addForecastConfigSuccess } = useAppSelector(
        (state) => state.dashboard
    );

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const {
        data: warehouseData,
        search: warehouseSearch,
        handleSearch: handleWarehouseSearch,
        handleDebouncedChange: handleWarehouseDebouncedChange,
    } = useGetAllWarehouses();

    const handleWarehouseChange = (e) => {
        const { value } = e.target;
        const arrayValue = value !== "" && value?.split("-");
        setWarehouse(value);
    };

    const handleProductSearch = (e) => {
        const { value } = e.target;
        setProductSearch(value);
    };

    const handleProductSearchDebounced = debounce((e) => {
        const { value } = e.target;
        setDebouncedProductSearch(value);
    }, 500);

    const handleBundleSearch = (e) => {
        const { value } = e.target;
        setBundleSearch(value);
    };

    const handleBundleSearchDebounced = debounce((e) => {
        const { value } = e.target;
        setDebouncedBundleSearch(value);
    }, 500);

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    useEffect(() => {
        if (durationFilter === "custom" && customValue[0] !== customValue[1]) {
            const dates = [
                moment(customValue?.[0]).format("MMM DD YYYY"),
                moment(customValue?.[1]).format("MMM DD YYYY"),
            ];

            setStartAndEndDate([dates?.[0], dates?.[1]]);
        }
    }, [durationFilter, customValue]);

    const forecastGraphSkuTabs = [
        {
            title: "Single SKUs",
            component: (
                <SingleForecastGraph
                    activeForecastViewType={activeForecastViewType}
                    salesChannel={salesChannel as string[]}
                    isConfigureForecast={configureForecast}
                />
            ),
        },
        {
            title: "Bundled SKUs",
            component: (
                <BundledForecastGraph
                    activeForecastViewType={activeForecastViewType}
                    activeSkuType={activeSkuType}
                    salesChannel={salesChannel as string[]}
                    isConfigureForecast={configureForecast}
                />
            ),
        },
    ];

    const forecastTableSkuTabs = [
        {
            title: "Single SKUs",
            component: (
                <SingleForecastTable
                    debouncedProductSearch={debouncedProductSearch}
                    startDate={startDate}
                    selectedDate={selectedDate}
                    warehouseId={warehouse?.split("-")[1] || ""}
                    singleProductChannelFilter={singleProductChannelFilter}
                />
            ),
        },
        {
            title: "Bundled SKUs",
            component: (
                <BundledForecastTable
                    activeForecastViewType={activeForecastViewType}
                    activeSkuType={activeSkuType}
                    debouncedProductSearch={debouncedBundleSearch}
                    startDate={startDate}
                    selectedDate={selectedBundleDate}
                    warehouseId={warehouse?.split("-")[1] || ""}
                    bundleProductChannelFilter={bundleProductChannelFilter}
                    salesChannel={salesChannel as string[]}
                />
            ),
        },
    ];

    return (
        <div>
            <div className="relative lg:border lg:border-slate-200 lg:rounded-xl py-[15px] lg:px-5 lg:mt-[16px]">
                <div className="flex">
                    <div className="flex items-center gap-6">
                        <p className="text-g-75 font-rocGroteskMedium text-sm lg:text-lg mb-4 lg:mb-0">
                            {moment().format("MMMM")}'s Forecast
                        </p>

                        <div className="bg-slate-50 !w-fit p-1 flex gap-2 font-rocGroteskMedium font-sm  max-lg:mb-0">
                            {forecastTabs?.map((tab) => {
                                return (
                                    <button
                                        // disabled={configureForecast}
                                        key={tab?.id}
                                        className={`w-auto  py-[7px] px-4  text-sm ${
                                            activeForecastViewType === tab?.id &&
                                            "!bg-white rounded-[6px] shadow-buttonShadow text-slate-900"
                                        } text-slate-500 `}
                                        onClick={() => {
                                            activeForecastViewType !== tab.id &&
                                                setActiveForecastViewType(tab?.id);
                                            setConfigureForecast(false);
                                        }}
                                    >
                                        {tab?.title}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex-1"></div>

                    <div className="flex gap-2">
                        {!configureForecast && (
                            <>
                                {/* <Button
                                    btnText="Create Sales target"
                                    btnClassname="!bg-[#F4F5F7] cursor-pointer !text-[13px] !px-[12px] !py-2.5 rounded-[5px] !h-[42px]"
                                    onClick={() => {
                                        if (targetAllyAgent?.activeAgent?.isActive) {
                                            setAgentModal(true);
                                            console.log("targetAllyAgent is active");
                                            setOpenSalesTargetAgent(false);
                                        } else {
                                            setAgentModal(false);
                                            setOpenSalesTargetAgent(true);
                                        }
                                    }}
                                /> */}

                                <Button
                                    btnText="Forecast demand for a new product"
                                    btnClassname="!bg-[#F4F5F7] cursor-pointer w-full !text-[13px] !px-[12px] !py-2.5 rounded-[5px] !h-[42px] !whitespace-nowrap"
                                    onClick={() => {
                                        toggleForecastNewProductModal();
                                    }}
                                />

                                <Button
                                    btnText="Configure forecast"
                                    btnClassname="!bg-[#0AAD0A] cursor-pointer !text-white !text-[13px] !whitespace-nowrap !px-[12px] !py-2.5 rounded-[5px] !h-[42px] !font-rocGroteskRegular"
                                    icon={<i className="ri-edit-2-fill text-base"></i>}
                                    btnPermission={[
                                        AccountPermissions.all_access,
                                        AccountPermissions.view_dashboard,
                                    ]}
                                    isLoading={addingForecastConfig}
                                    onClick={() => {
                                        setActiveForecastViewType(FORECAST_VIEW_TYPE.TABLE);
                                        setConfigureForecast(true);
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="mt-4">
                    <div className="flex items-center justify-between py-[13px] border-t">
                        <div className="flex items-center space-x-2.5">
                            {forecastGraphSkuTabs?.map((item, idx) => {
                                return (
                                    <span
                                        onClick={() => {
                                            setActiveSkuType(idx);
                                        }}
                                        className={`text-sm font-rocGroteskMedium cursor-pointer pb-2.5 ${
                                            activeSkuType === idx
                                                ? "text-gm-50 border-b-2 border-[#FFB903]"
                                                : "text-tradeally-neutral-200"
                                        }`}
                                    >
                                        {item?.title}
                                    </span>
                                );
                            })}
                        </div>

                        {activeForecastViewType === FORECAST_VIEW_TYPE.TABLE &&
                            activeSkuType === FORECAST_SKU_TYPE.SINGLE &&
                            !configureForecast && (
                                <div className="flex items-center space-x-2.5">
                                    <Dropdown
                                        handleChange={(name, value) =>
                                            handleWarehouseChange({
                                                target: { name, value },
                                            })
                                        }
                                        search={
                                            <div ref={warehouseRef} className="">
                                                <TextInput
                                                    value={warehouseSearch}
                                                    name={"search"}
                                                    type={"text"}
                                                    required={false}
                                                    onChange={handleWarehouseSearch}
                                                    onInput={handleWarehouseDebouncedChange}
                                                    inputPlaceholder="Search warehouse"
                                                    containerClassname=""
                                                    inputContainerClassname={"!h-[40px] "}
                                                />
                                            </div>
                                        }
                                        dropdown={
                                            <div className="flex items-center justify-between lg:rounded-[5px] lg:shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100">
                                                <div
                                                    className={`text-slate-800 font-rocGroteskMedium text-[13px] inline-block whitespace-nowrap capitalize`}
                                                >
                                                    {warehouse
                                                        ? truncate(warehouse?.split("-")[0], {
                                                              length: 12,
                                                          })
                                                        : "All warehouses"}
                                                </div>
                                                <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[5px] !max-h-[200px]
                                         !w-[200px] !min-w-min !pt-0
                            `}
                                        name="valueRange"
                                        dropdownOptions={[
                                            {
                                                label: (
                                                    <div
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse === ""
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        All Warehouses
                                                    </div>
                                                ),
                                                value: "",
                                            },
                                            ...warehouseData?.map((item, idx) => ({
                                                label: (
                                                    <div
                                                        key={idx}
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                    border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse?.split("-")[1] ===
                                                                item?._id
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        {item?.warehouseName}
                                                    </div>
                                                ),
                                                value: item?.warehouseName + "-" + item?._id,
                                            })),
                                        ]}
                                    />

                                    <MultiChannelSelect
                                        salesChannel={salesChannel}
                                        value={singleProductChannelFilter}
                                        onChange={(value) => setSingleProductChannelFilter(value)}
                                        isLoading={salesChannelLoading}
                                    />

                                    <Dropdown
                                        handleChange={(name, value) => {
                                            // configHandleChannelChange(value);
                                            // handleChangeValue({
                                            //     target: { name, value },
                                            // });
                                            setSelectedDate(value);
                                        }}
                                        dropdown={
                                            <div className="flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                                <div
                                                    className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block whitespace-nowrap w-fit `}
                                                >
                                                    {startDate && selectedDate
                                                        ? `${moment(selectedDate).diff(
                                                              moment(startDate),
                                                              "months"
                                                          )} months (${moment(startDate).format(
                                                              "MMM YYYY"
                                                          )} - ${moment(selectedDate).format(
                                                              "MMM YYYY"
                                                          )})`
                                                        : "6 months"}
                                                </div>
                                                <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min
                                        `}
                                        name="valueRange"
                                        dropdownOptions={forecastPeriodOptions}
                                    />
                                    <TextInput
                                        name="search"
                                        type="text"
                                        value={productSearch}
                                        onChange={handleProductSearch}
                                        onInput={handleProductSearchDebounced}
                                        inputPlaceholder={"Search products"}
                                        inputClassName={
                                            "!h-[36px]  pl-[0px] !mb-0 !font-rocGroteskMedium !text-slate-400 text-[13px] "
                                        }
                                        leftIcon={
                                            <i className="ri-search-line text-slate-400 mt-[2px]"></i>
                                        }
                                        inputContainerClassname={
                                            "!rounded-[4px] !bg-slate-50 !h-[36px] !w-[300px] !pl-[20px] !border !border-slate-100"
                                        }
                                    />
                                </div>
                            )}

                        {activeForecastViewType === FORECAST_VIEW_TYPE.TABLE &&
                            activeSkuType === FORECAST_SKU_TYPE.BUNDLED &&
                            !configureForecast && (
                                <div className="flex items-center space-x-2.5">
                                    <Dropdown
                                        handleChange={(name, value) =>
                                            handleWarehouseChange({
                                                target: { name, value },
                                            })
                                        }
                                        search={
                                            <div ref={warehouseRef} className="">
                                                <TextInput
                                                    value={warehouseSearch}
                                                    name={"search"}
                                                    type={"text"}
                                                    required={false}
                                                    onChange={handleWarehouseSearch}
                                                    onInput={handleWarehouseDebouncedChange}
                                                    inputPlaceholder="Search warehouse"
                                                    containerClassname=""
                                                    inputContainerClassname={"!h-[40px] "}
                                                />
                                            </div>
                                        }
                                        dropdown={
                                            <div className="flex items-center justify-between lg:rounded-[5px] lg:shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100">
                                                <div
                                                    className={`text-slate-800 font-rocGroteskMedium text-[13px] inline-block whitespace-nowrap capitalize`}
                                                >
                                                    {warehouse
                                                        ? truncate(warehouse?.split("-")[0], {
                                                              length: 12,
                                                          })
                                                        : "All warehouses"}
                                                </div>
                                                <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[5px] !max-h-[200px]
                                         !w-[200px] !min-w-min !pt-0
                            `}
                                        name="valueRange"
                                        dropdownOptions={[
                                            {
                                                label: (
                                                    <div
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse === ""
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        All Warehouses
                                                    </div>
                                                ),
                                                value: "",
                                            },
                                            ...warehouseData?.map((item, idx) => ({
                                                label: (
                                                    <div
                                                        key={idx}
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                    border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse?.split("-")[1] ===
                                                                item?._id
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        {item?.warehouseName}
                                                    </div>
                                                ),
                                                value: item?.warehouseName + "-" + item?._id,
                                            })),
                                        ]}
                                    />

                                    <MultiChannelSelect
                                        salesChannel={salesChannel}
                                        value={bundleProductChannelFilter}
                                        onChange={(value) => setBundleProductChannelFilter(value)}
                                        isLoading={salesChannelLoading}
                                    />

                                    <Dropdown
                                        handleChange={(name, value) => {
                                            setSelectedBundleDate(value);
                                        }}
                                        dropdown={
                                            <div className="flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                                <div
                                                    className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block whitespace-nowrap w-fit `}
                                                >
                                                    {startDate && selectedBundleDate
                                                        ? `${moment(selectedBundleDate).diff(
                                                              moment(startDate),
                                                              "months"
                                                          )} months (${moment(startDate).format(
                                                              "MMM YYYY"
                                                          )} - ${moment(selectedBundleDate).format(
                                                              "MMM YYYY"
                                                          )})`
                                                        : "6 months"}
                                                </div>
                                                <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min
                                        `}
                                        name="valueRange"
                                        dropdownOptions={forecastPeriodOptions}
                                    />
                                    <TextInput
                                        name="search"
                                        type="text"
                                        value={bundleSearch}
                                        onChange={handleBundleSearch}
                                        onInput={handleBundleSearchDebounced}
                                        inputPlaceholder={"Search products"}
                                        inputClassName={
                                            "!h-[36px]  pl-[0px] !mb-0 !font-rocGroteskMedium !text-slate-400 text-[13px] "
                                        }
                                        leftIcon={
                                            <i className="ri-search-line text-slate-400 mt-[2px]"></i>
                                        }
                                        inputContainerClassname={
                                            "!rounded-[4px] !bg-slate-50 !h-[36px] !w-[300px] !pl-[20px] !border !border-slate-100"
                                        }
                                    />
                                </div>
                            )}
                    </div>

                    {activeForecastViewType === FORECAST_VIEW_TYPE.GRAPH &&
                        !configureForecast &&
                        forecastGraphSkuTabs[activeSkuType]?.component}

                    {activeForecastViewType === FORECAST_VIEW_TYPE.TABLE &&
                        !configureForecast &&
                        forecastTableSkuTabs[activeSkuType]?.component}

                    {configureForecast && (
                        <ForecastConfiguration
                            activeForecastViewType={activeForecastViewType}
                            salesChannel={salesChannel}
                            onCloseConfig={() => {
                                setConfigureForecast(false);
                            }}
                            skuType={activeSkuType}
                        />
                    )}
                </div>
            </div>
            {openForecastNewProductModal && (
                <ForecastNewProductModal
                    open={openForecastNewProductModal}
                    closeModal={() => {
                        toggleForecastNewProductModal();
                    }}
                />
            )}

            <SalesTargetAgentModal
                open={agentModal}
                setOpen={setAgentModal}
                openAgentModal={openSalesTargetAgent}
                setOpenAgentModal={setOpenSalesTargetAgent}
            />
            <SalesTargetAgent open={agentModal} setOpen={setAgentModal} />
        </div>
    );
};

export default FinishedGoodsForecast;
